import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Save,
  Plus,
  Link,
  Unlink,
  Rocket,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  clientsAuthApi,
  facilitiesApi,
  facilityServicesAuthApi,
  authFetch,
} from "../../utils/api";
import FacilityDetail from "../client-management/FacilityDetail";
import {
  DEFAULT_INVITE_DESTINATION,
  INVITE_DESTINATIONS,
  PRODUCT_CODES,
} from "../../constants";

const VERTICALS = [
  { value: "political", label: "Political & Public Affairs" },
  { value: "healthcare", label: "Healthcare" },
  { value: "legal", label: "Legal" },
  { value: "finance", label: "Finance" },
  { value: "retail", label: "Retail / E-Commerce" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "construction", label: "Construction" },
];

const PINECONE_INDEXES = [
  { value: "gop", label: "GOP (Political)" },
  { value: "ltn", label: "LTN (Liberty Tree)" },
  { value: "email2", label: "Email2 (General)" },
  { value: "trm", label: "TRM (Recruiting)" },
];

const INDEX_VERTICAL_MAP = {
  gop: "political",
  ltn: "political",
  email2: "retail",
  trm: "retail",
};

const EMPTY_CREATE_FORM = {
  display_name: "",
  facility_id: "",
  state: "",
  agent_name: "Lidy",
  brand_color: "#2563eb",
  pinecone_index: "gop",
  vertical_id: "political",
  template_code: "",
};
const SERVICE_STATUSES = [
  "provisioning",
  "active",
  "suspended",
  "decommissioned",
];

const ClientDetail = ({ client, onBack, onSaved }) => {
  // Where invited users land. Not window.location.origin: that sent an
  // invited user to whichever console the admin was in.
  const [inviteDestination, setInviteDestination] = useState(
    DEFAULT_INVITE_DESTINATION,
  );
  const [selectedFacility, setSelectedFacility] = useState(null);

  // ── CLIENT DETAILS ────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: client.name || "",
    slug: client.slug || "",
    description: client.description || "",
    is_active: client.is_active ?? true,
  });
  const [savingClient, setSavingClient] = useState(false);
  const [clientError, setClientError] = useState(null);
  const [clientSaved, setClientSaved] = useState(false);

  const handleSaveClient = async () => {
    setSavingClient(true);
    setClientError(null);
    const result = await clientsAuthApi.update(client.id, formData);
    setSavingClient(false);
    if (result && result.error) {
      setClientError(result.error);
    } else {
      setClientSaved(true);
      setTimeout(() => setClientSaved(false), 2000);
      if (onSaved) onSaved();
    }
  };

  // ── FACILITIES ────────────────────────────────────────────────────
  const [facilities, setFacilities] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [facilityError, setFacilityError] = useState(null);
  const [showLinkFacility, setShowLinkFacility] = useState(false);
  const [linkingFacility, setLinkingFacility] = useState(null);
  const [linkSearch, setLinkSearch] = useState("");

  const fetchFacilities = async () => {
    setLoadingFacilities(true);
    const data = await clientsAuthApi.getFacilities(client.id);
    setFacilities(data);
    setLoadingFacilities(false);
  };

  const fetchAllFacilities = async () => {
    const data = await facilitiesApi.getAll();
    setAllFacilities(data);
  };

  useEffect(() => {
    fetchFacilities();
  }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showLinkFacility) fetchAllFacilities();
  }, [showLinkFacility]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLinkFacility = async (facilityId) => {
    setLinkingFacility(facilityId);
    setFacilityError(null);
    const result = await clientsAuthApi.linkFacility(facilityId, client.id);
    setLinkingFacility(null);
    if (result && result.error) {
      setFacilityError(result.error);
    } else {
      setShowLinkFacility(false);
      setLinkSearch("");
      fetchFacilities();
    }
  };

  const handleUnlinkFacility = async (facilityId) => {
    if (!window.confirm("Remove this facility from the client?")) return;
    setLinkingFacility(facilityId);
    setFacilityError(null);
    const result = await clientsAuthApi.linkFacility(facilityId, null);
    setLinkingFacility(null);
    if (result && result.error) {
      setFacilityError(result.error);
    } else {
      fetchFacilities();
    }
  };

  // facilities not yet linked to this client
  const unlinkedFacilities = allFacilities.filter(
    (f) =>
      !facilities.find(
        (lf) =>
          (lf.facility_id || lf.facilityId) === (f.facility_id || f.facilityId),
      ) &&
      (f.facility_id || f.facilityId || "")
        .toLowerCase()
        .includes(linkSearch.toLowerCase()),
  );

  // ── CREATE NEW FACILITY ───────────────────────────────────────────
  const [showCreateFacility, setShowCreateFacility] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [templates, setTemplates] = useState([]);
  const [creatingFacility, setCreatingFacility] = useState(false);
  const [createError, setCreateError] = useState(null);

  const deriveId = (name) =>
    name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 12);

  const handleCreateNameChange = (name) => {
    setCreateForm((prev) => ({
      ...prev,
      display_name: name,
      facility_id:
        !prev.facility_id || prev.facility_id === deriveId(prev.display_name)
          ? deriveId(name)
          : prev.facility_id,
    }));
  };

  useEffect(() => {
    if (showCreateFacility && templates.length === 0) {
      authFetch("/kb-templates")
        .then((data) => setTemplates(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, [showCreateFacility]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateFacility = async () => {
    setCreatingFacility(true);
    setCreateError(null);
    try {
      const data = await authFetch("/provision-facility", {
        method: "POST",
        body: JSON.stringify({
          ...createForm,
          facility_id: createForm.facility_id.toUpperCase(),
          state: createForm.state.toUpperCase(),
          client_id: client.id,
        }),
      });
      if (data && data.error) throw new Error(data.error);
      setShowCreateFacility(false);
      setCreateForm(EMPTY_CREATE_FORM);
      fetchFacilities();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreatingFacility(false);
    }
  };

  // ── SERVICES ─────────────────────────────────────────────────────
  // Services are shown per facility inside the facilities grid via inline expand
  const [expandedFacility, setExpandedFacility] = useState(null);
  const [facilityServices, setFacilityServices] = useState({});
  const [editingService, setEditingService] = useState(null);
  const [serviceEdits, setServiceEdits] = useState({});
  const [savingService, setSavingService] = useState(null);
  const [serviceError, setServiceError] = useState(null);
  const [showAddService, setShowAddService] = useState(null); // facility_id
  const [newService, setNewService] = useState({
    service_type: "rag",
    status: "provisioning",
    config: "{}",
  });

  const fetchFacilityServices = async (facilityId) => {
    const data = await facilityServicesAuthApi.getByFacility(facilityId);
    setFacilityServices((prev) => ({ ...prev, [facilityId]: data }));
  };

  const handleToggleFacility = (facilityId) => {
    if (expandedFacility === facilityId) {
      setExpandedFacility(null);
    } else {
      setExpandedFacility(facilityId);
      if (!facilityServices[facilityId]) fetchFacilityServices(facilityId);
    }
  };

  const handleEditService = (svc) => {
    setEditingService(svc.id);
    setServiceEdits({
      status: svc.status,
      config:
        typeof svc.config === "object"
          ? JSON.stringify(svc.config, null, 2)
          : svc.config || "{}",
    });
  };

  const handleSaveService = async (svc, facilityId) => {
    setSavingService(svc.id);
    setServiceError(null);
    let config;
    try {
      config = JSON.parse(serviceEdits.config || "{}");
    } catch {
      setServiceError("Invalid JSON in config");
      setSavingService(null);
      return;
    }
    const result = await facilityServicesAuthApi.set({
      id: svc.id,
      facility_id: facilityId,
      service_type: svc.service_type,
      status: serviceEdits.status,
      config,
    });
    setSavingService(null);
    if (result && result.error) {
      setServiceError(result.error);
    } else {
      setEditingService(null);
      fetchFacilityServices(facilityId);
    }
  };

  const handleAddService = async (facilityId) => {
    setSavingService("new");
    setServiceError(null);
    let config;
    try {
      config = JSON.parse(newService.config || "{}");
    } catch {
      setServiceError("Invalid JSON in config");
      setSavingService(null);
      return;
    }
    const result = await facilityServicesAuthApi.set({
      facility_id: facilityId,
      service_type: newService.service_type,
      status: newService.status,
      config,
    });
    setSavingService(null);
    if (result && result.error) {
      setServiceError(result.error);
    } else {
      setShowAddService(null);
      setNewService({
        service_type: "rag",
        status: "provisioning",
        config: "{}",
      });
      fetchFacilityServices(facilityId);
    }
  };

  // ── USERS ─────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    service_type: "rag",
    access_role: "user",
    facility_id: "",
  });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    const data = await clientsAuthApi.getUsers(client.id);
    setUsers(data);
    setLoadingUsers(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleUser = async (user) => {
    setTogglingUser(user.id);
    setUsersError(null);
    const result = await authFetch("/facility-users/toggle", {
      method: "POST",
      body: JSON.stringify({ user_id: user.id, is_active: !user.is_active }),
    });
    setTogglingUser(null);
    if (result && result.error) {
      setUsersError(result.error);
    } else {
      fetchUsers();
    }
  };

  const handleInvite = async () => {
    setInviting(true);
    setInviteError(null);
    const result = await authFetch("/facility-users/invite", {
      method: "POST",
      body: JSON.stringify({ ...inviteData, app_url: inviteDestination }),
    });
    setInviting(false);
    if (result && result.error) {
      setInviteError(result.error);
    } else {
      setShowInvite(false);
      setInviteData({
        email: "",
        first_name: "",
        last_name: "",
        service_type: "rag",
        access_role: "user",
        facility_id: "",
      });
      fetchUsers();
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────

  // Drill into facility detail
  if (selectedFacility) {
    return (
      <FacilityDetail
        facility={selectedFacility}
        onBack={() => setSelectedFacility(null)}
        onSaved={fetchFacilities}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {client.name}
          </h3>
          <p className="text-sm text-slate-400 font-mono">{client.slug}</p>
        </div>
      </div>

      {/* ── CLIENT DETAILS ── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h4 className="text-base font-semibold text-slate-800 mb-4">
          Client Details
        </h4>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Slug *
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm text-slate-600">Active</span>
            </label>
          </div>
          {clientError && <p className="text-sm text-red-600">{clientError}</p>}
          <div className="flex justify-end">
            <button
              onClick={handleSaveClient}
              disabled={savingClient}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>
                {savingClient
                  ? "Saving..."
                  : clientSaved
                    ? "Saved!"
                    : "Save Client"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── FACILITIES SECTION ── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-slate-800">Facilities</h4>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setShowCreateFacility(!showCreateFacility);
                setShowLinkFacility(false);
                setCreateError(null);
              }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center space-x-1"
            >
              <Rocket className="w-4 h-4" />
              <span>Create New</span>
              {showCreateFacility ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>
            <button
              onClick={() => {
                setShowLinkFacility(!showLinkFacility);
                setShowCreateFacility(false);
                setLinkSearch("");
              }}
              className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center space-x-1"
            >
              <Link className="w-4 h-4" />
              <span>Link Existing</span>
            </button>
          </div>
        </div>

        {facilityError && (
          <p className="text-sm text-red-600 mb-3">{facilityError}</p>
        )}

        {/* ── CREATE NEW FACILITY PANEL ── */}
        {showCreateFacility && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
            <h5 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              New Facility for {client.name}
            </h5>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={createForm.display_name}
                  onChange={(e) => handleCreateNameChange(e.target.value)}
                  placeholder="Arizona Legislative District 4"
                  maxLength={100}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Facility ID *
                </label>
                <input
                  type="text"
                  value={createForm.facility_id}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      facility_id: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9_]/g, ""),
                    })
                  }
                  placeholder="AZLD4"
                  maxLength={20}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  State *
                </label>
                <input
                  type="text"
                  value={createForm.state}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      state: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 2),
                    })
                  }
                  placeholder="AZ"
                  maxLength={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={createForm.agent_name}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, agent_name: e.target.value })
                  }
                  placeholder="Lidy"
                  maxLength={30}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Pinecone Index
                </label>
                <select
                  value={createForm.pinecone_index}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      pinecone_index: e.target.value,
                      vertical_id:
                        INDEX_VERTICAL_MAP[e.target.value] || "retail",
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {PINECONE_INDEXES.map((idx) => (
                    <option key={idx.value} value={idx.value}>
                      {idx.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Vertical
                </label>
                <select
                  value={createForm.vertical_id}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      vertical_id: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {VERTICALS.map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Brand Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={createForm.brand_color}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        brand_color: e.target.value,
                      })
                    }
                    className="w-9 h-9 rounded border border-slate-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={createForm.brand_color}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        brand_color: e.target.value,
                      })
                    }
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  KB Template
                </label>
                <select
                  value={createForm.template_code}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      template_code: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">No template</option>
                  {templates.map((t) => (
                    <option
                      key={t.template_code || t.id}
                      value={t.template_code}
                    >
                      {t.template_name || t.template_code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {createError && (
              <p className="text-xs text-red-600">{createError}</p>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCreateFacility}
                disabled={
                  creatingFacility ||
                  !createForm.display_name ||
                  !createForm.facility_id ||
                  !createForm.state
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <Rocket className="w-4 h-4" />
                <span>
                  {creatingFacility ? "Provisioning..." : "Provision & Link"}
                </span>
              </button>
              <button
                onClick={() => {
                  setShowCreateFacility(false);
                  setCreateForm(EMPTY_CREATE_FORM);
                  setCreateError(null);
                }}
                className="px-3 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── LINK EXISTING PANEL ── */}
        {showLinkFacility && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <h5 className="text-sm font-medium text-slate-700">
              Link Existing Facility
            </h5>
            <input
              type="text"
              placeholder="Search by facility ID or name..."
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <div className="max-h-48 overflow-y-auto space-y-1">
              {unlinkedFacilities.length === 0 ? (
                <p className="text-xs text-slate-400 py-2">
                  No unlinked facilities match.
                </p>
              ) : (
                unlinkedFacilities.map((f) => {
                  const fid = f.facility_id || f.facilityId;
                  const fname = f.facility_name || f.facilityName || fid;
                  return (
                    <div
                      key={fid}
                      className="flex items-center justify-between px-3 py-2 bg-white rounded border border-slate-200 hover:bg-slate-50"
                    >
                      <div>
                        <span className="text-sm font-medium text-slate-800">
                          {fname}
                        </span>
                        <span className="text-xs text-slate-400 font-mono ml-2">
                          {fid}
                        </span>
                      </div>
                      <button
                        onClick={() => handleLinkFacility(fid)}
                        disabled={linkingFacility === fid}
                        className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        {linkingFacility === fid ? "..." : "Link"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            <button
              onClick={() => {
                setShowLinkFacility(false);
                setLinkSearch("");
              }}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        )}

        {loadingFacilities ? (
          <p className="text-sm text-slate-400">Loading facilities...</p>
        ) : facilities.length === 0 ? (
          <p className="text-sm text-slate-400">
            No facilities linked to this client.
          </p>
        ) : (
          <div className="space-y-2">
            {facilities.map((facility) => {
              const fid = facility.facility_id || facility.facilityId;
              const fname =
                facility.facility_name || facility.facilityName || fid;
              const isExpanded = expandedFacility === fid;
              const svcs = facilityServices[fid] || [];

              return (
                <div
                  key={fid}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Facility row */}
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <button
                      className="flex-1 text-left flex items-center space-x-3"
                      onClick={() => handleToggleFacility(fid)}
                    >
                      <div>
                        <span className="text-sm font-medium text-slate-800">
                          {fname}
                        </span>
                        <span className="text-xs text-slate-400 font-mono ml-2">
                          {fid}
                        </span>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          facility.status === "active"
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {facility.status || "active"}
                      </span>
                    </button>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedFacility(facility)}
                        className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-xs font-medium hover:bg-white"
                      >
                        Edit Facility
                      </button>
                      <button
                        onClick={() => handleUnlinkFacility(fid)}
                        disabled={linkingFacility === fid}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Unlink facility"
                      >
                        <Unlink className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded services */}
                  {isExpanded && (
                    <div className="px-4 py-3 border-t border-slate-200 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <h6 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          Services
                        </h6>
                        <button
                          onClick={() => setShowAddService(fid)}
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Add</span>
                        </button>
                      </div>

                      {serviceError && (
                        <p className="text-xs text-red-600 mb-2">
                          {serviceError}
                        </p>
                      )}

                      {showAddService === fid && (
                        <div className="mb-3 p-3 bg-slate-50 rounded border border-slate-200 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={newService.service_type}
                              onChange={(e) =>
                                setNewService({
                                  ...newService,
                                  service_type: e.target.value,
                                })
                              }
                              className="px-2 py-1.5 border border-slate-300 rounded text-sm"
                            >
                              {PRODUCT_CODES.filter(
                                (p) => p.type !== "combo",
                              ).map((t) => (
                                <option key={t.value} value={t.type}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                            <select
                              value={newService.status}
                              onChange={(e) =>
                                setNewService({
                                  ...newService,
                                  status: e.target.value,
                                })
                              }
                              className="px-2 py-1.5 border border-slate-300 rounded text-sm"
                            >
                              {SERVICE_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                          <textarea
                            value={newService.config}
                            onChange={(e) =>
                              setNewService({
                                ...newService,
                                config: e.target.value,
                              })
                            }
                            rows={2}
                            placeholder="Config JSON"
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs font-mono"
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleAddService(fid)}
                              disabled={savingService === "new"}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                            >
                              {savingService === "new" ? "..." : "Add"}
                            </button>
                            <button
                              onClick={() => setShowAddService(null)}
                              className="px-3 py-1 border border-slate-300 text-slate-600 rounded text-xs hover:bg-slate-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {svcs.length === 0 ? (
                        <p className="text-xs text-slate-400">
                          No services configured.
                        </p>
                      ) : (
                        <table className="min-w-full text-xs divide-y divide-slate-100">
                          <thead>
                            <tr className="text-slate-400 uppercase text-[10px]">
                              <th className="pb-1 text-left pr-4">Service</th>
                              <th className="pb-1 text-left pr-4">Status</th>
                              <th className="pb-1 text-left pr-4">Config</th>
                              <th className="pb-1 text-left"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {svcs.map((svc) => (
                              <tr key={svc.id} className="hover:bg-slate-50">
                                <td className="py-1.5 pr-4 font-medium uppercase text-slate-700">
                                  {svc.service_type}
                                </td>
                                <td className="py-1.5 pr-4">
                                  {editingService === svc.id ? (
                                    <select
                                      value={serviceEdits.status}
                                      onChange={(e) =>
                                        setServiceEdits({
                                          ...serviceEdits,
                                          status: e.target.value,
                                        })
                                      }
                                      className="px-1.5 py-0.5 border border-slate-300 rounded text-xs"
                                    >
                                      {SERVICE_STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                          {s}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span
                                      className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                        svc.status === "active"
                                          ? "bg-green-100 text-green-700"
                                          : svc.status === "provisioning"
                                            ? "bg-yellow-100 text-yellow-700"
                                            : "bg-slate-100 text-slate-600"
                                      }`}
                                    >
                                      {svc.status}
                                    </span>
                                  )}
                                </td>
                                <td className="py-1.5 pr-4 max-w-xs">
                                  {editingService === svc.id ? (
                                    <textarea
                                      value={serviceEdits.config}
                                      onChange={(e) =>
                                        setServiceEdits({
                                          ...serviceEdits,
                                          config: e.target.value,
                                        })
                                      }
                                      rows={3}
                                      className="w-full px-1.5 py-1 border border-slate-300 rounded text-xs font-mono"
                                    />
                                  ) : (
                                    <span className="truncate text-slate-400 font-mono">
                                      {typeof svc.config === "object"
                                        ? JSON.stringify(svc.config)
                                        : svc.config || "{}"}
                                    </span>
                                  )}
                                </td>
                                <td className="py-1.5">
                                  {editingService === svc.id ? (
                                    <div className="flex space-x-1">
                                      <button
                                        onClick={() =>
                                          handleSaveService(svc, fid)
                                        }
                                        disabled={savingService === svc.id}
                                        className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] hover:bg-blue-700 disabled:opacity-50"
                                      >
                                        {savingService === svc.id
                                          ? "..."
                                          : "Save"}
                                      </button>
                                      <button
                                        onClick={() => setEditingService(null)}
                                        className="px-2 py-0.5 border border-slate-300 text-slate-600 rounded text-[10px]"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleEditService(svc)}
                                      className="px-2 py-0.5 border border-slate-300 text-slate-600 rounded text-[10px] hover:bg-slate-50"
                                    >
                                      Edit
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── USERS SECTION ── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-slate-800">
            Client Users
          </h4>
          <button
            onClick={() => setShowInvite(true)}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center space-x-1"
          >
            <Plus className="w-4 h-4" />
            <span>Invite User</span>
          </button>
        </div>

        {usersError && (
          <p className="text-sm text-red-600 mb-3">{usersError}</p>
        )}

        {showInvite && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <h5 className="text-sm font-medium text-slate-700">Invite User</h5>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">
                  Which product is this for?
                </label>
                <select
                  value={inviteDestination}
                  onChange={(e) => setInviteDestination(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  {INVITE_DESTINATIONS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Their set-password email and sign-in link point here.
                </p>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={inviteData.first_name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, first_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={inviteData.last_name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, last_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Facility
                </label>
                <select
                  value={inviteData.facility_id}
                  onChange={(e) =>
                    setInviteData({
                      ...inviteData,
                      facility_id: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="">— select facility —</option>
                  {facilities.map((f) => {
                    const fid = f.facility_id || f.facilityId;
                    const fname = f.facility_name || f.facilityName || fid;
                    return (
                      <option key={fid} value={fid}>
                        {fname}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Access Role
                </label>
                <select
                  value={inviteData.access_role}
                  onChange={(e) =>
                    setInviteData({
                      ...inviteData,
                      access_role: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  {[
                    "none",
                    "viewer",
                    "user",
                    "editor",
                    "approver",
                    "admin",
                  ].map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {inviteError && (
              <p className="text-xs text-red-600">{inviteError}</p>
            )}
            <div className="flex space-x-2">
              <button
                onClick={handleInvite}
                disabled={inviting || !inviteData.email}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {inviting ? "Inviting..." : "Send Invite"}
              </button>
              <button
                onClick={() => {
                  setShowInvite(false);
                  setInviteError(null);
                }}
                className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loadingUsers ? (
          <p className="text-sm text-slate-400">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-slate-400">No users on this client.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    User
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Platform Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Client Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-xs text-slate-400">{user.email}</div>
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-600">
                      {user.role}
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-600">
                      {user.client_role || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleUser(user)}
                        disabled={togglingUser === user.id}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                          user.is_active
                            ? "border border-red-200 text-red-600 hover:bg-red-50"
                            : "border border-green-200 text-green-600 hover:bg-green-50"
                        }`}
                      >
                        {togglingUser === user.id
                          ? "..."
                          : user.is_active
                            ? "Disable"
                            : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;
